import { Checkbox } from '@hologram-dimension/checkbox';
import { InlineNotification } from '@hologram-dimension/inline-notification';
import usePlans from 'api/legacy/organization/usePlans';
import { filterLabels } from 'components/SimInventoryFilters/shared';
import isArray from 'lodash/isArray';
import {
  type ChangeEventHandler,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import useSimInventoryStoredFilters from 'state/SimInventory/useSimInventoryStoredFilters';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type { OnSimsModified } from 'types/common';
import FilterAccordion, { type FilterProps } from '../../FilterAccordion/FilterAccordion';
import filterStyles from '../filterStyles.module.css';
import styles from './SimPlanFilter.module.css';

interface SimPlanFilterProps extends FilterProps {}

const SimPlanFilter = forwardRef<OnSimsModified, SimPlanFilterProps>(
  ({ form, onClear, segmentName, submit }, ref) => {
    const { register, setValue, getValues } = form;
    const { plans: storedSimPlans, organizationId: filterOrganizationId } =
      useSimInventoryStoredFilters();

    const defaultOrgId = useOrganization();
    const organizationId = segmentName === 'all' ? filterOrganizationId : defaultOrgId;
    const hasOrganizationId = !!organizationId;

    const { plans, isLoading, error, mutate } = usePlans({
      organizationId,
      canLoad: hasOrganizationId,
    });

    // Refresh
    const refresh = useCallback(() => {
      if (!isLoading) {
        mutate();
      }
    }, [isLoading, mutate]);

    useImperativeHandle(ref, () => refresh, [refresh]);

    const selectedOptions = getValues('plans');

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(() => {
      submit();
    }, [submit]);

    const checkboxField = register('plans', {
      onChange: handleChange,
    });

    const handleAllChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        if (!setValue) {
          // eslint-disable-next-line no-console
          console.error('Unexpectedly missing setValue when toggling select all');
          return;
        }
        if (e.target.checked) {
          setValue(
            'plans',
            plans.map((plan) => `${plan.id}`)
          );
        } else {
          setValue('plans', undefined);
        }
        submit();
      },
      [plans, setValue, submit]
    );

    const isSet = !!storedSimPlans && isArray(storedSimPlans) && storedSimPlans.length > 0;

    const content = useMemo(() => {
      if (error) {
        return (
          <InlineNotification variant="system-error" style={{ display: 'inline' }}>
            We ran into an issue loading this filter. You can try to refresh.
          </InlineNotification>
        );
      }

      if (isLoading) {
        return <div className={styles.loadingText}>Loading...</div>;
      }

      if (!hasOrganizationId) {
        return <div className={styles.placeholderText}>Please select an organization.</div>;
      }

      return (
        <>
          <Checkbox
            label="Select all"
            value="all"
            key="all"
            onChange={handleAllChange}
            indeterminate={
              (selectedOptions?.length ?? 0) > 0 && selectedOptions!.length !== plans.length
            }
            defaultChecked={isSet && (selectedOptions?.length ?? 0) === plans.length}
          />
          <hr className={filterStyles.divider} />
          <div className={styles.optionWrapper}>
            {plans.map((plan) => (
              <Checkbox
                className={styles.planOption}
                label={`${plan.planName}`}
                title={`${plan.planName}`}
                value={`${plan.id}`}
                key={plan.id}
                defaultChecked={isSet && storedSimPlans.includes(plan.id)}
                {...checkboxField}
              />
            ))}
          </div>
        </>
      );
    }, [
      checkboxField,
      error,
      handleAllChange,
      hasOrganizationId,
      isLoading,
      isSet,
      plans,
      selectedOptions,
      storedSimPlans,
    ]);

    return (
      <FilterAccordion
        onClear={onClear}
        label={filterLabels.plans}
        isSet={isSet}
        defaultActive={isSet}
      >
        <div className={styles.wrapper}>{content}</div>
      </FilterAccordion>
    );
  }
);

export default SimPlanFilter;
